import styled from 'styled-components';
import { useEffect, useState, useCallback, useMemo } from 'react';

/* JS */
import * as Server from 'assets/js/Server';

import CustomerHead from "../../components/customer/CustomerHead";

/* Chart */
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';

export default function PestTrends() {
  // 1. 필터 및 파라미터 상태 관리
  const [param, setParam] = useState({
    type: 'Weekly',
    startDate: new Date().toISOString().split('T')[0] // 초기값: 오늘 날짜 (YYYY-MM-DD)
  });

  const [headquarter, setHeadquarter] = useState(null);
  const [customer, setCustomer] = useState([]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { left: 10, right: 10 },
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            boxWidth: 12,
            font: { size: 11 }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { stepSize: 5 }
        }
      }
    }),
    []
  );

  const colors = [
    'rgba(255, 99, 132, 1)',   // cockroaches
    'rgba(75, 192, 192, 1)',   // houseFlies
    'rgba(255, 206, 86, 1)',   // lizards
    'rgba(255, 159, 64, 1)',   // otherInsects
    'rgba(54, 162, 235, 1)',   // rodents
  ];

  const generateDataset = useCallback((label, data, colorIndex) => ({
    label,
    data,
    borderColor: colors[colorIndex],
    backgroundColor: colors[colorIndex].replace('1)', '0.1)'),
    borderWidth: 2,
    tension: 0.3,
    pointRadius: 3,
  }), []);

  // 2. param 변경 시마다 데이터 재호출
  useEffect(() => {
    getMainPageData();
  }, [param]);

  const getMainPageData = () => {
    // API 호출 시 현재 param 상태를 전달합니다.
    Server.sendGet('client/trend', param, getMainPageDataCallback).then();
  };

  const getMainPageDataCallback = res => {
    const resultList = res?.['resultList'] || [];

    if (resultList.length > 0) {
      const customerList = resultList.map(obj => {
        if (obj.custMstSeq === '20') {
          setHeadquarter(obj);
          return null;
        }

        const config = {
          labels: obj.captureList.cockroachesList.map(item => item.regDate),
          datasets: [
            generateDataset('Cockroaches', obj.captureList.cockroachesList.map(item => item.pestsCnt), 0),
            generateDataset('House Flies', obj.captureList.houseFliesList.map(item => item.pestsCnt), 1),
            generateDataset('Lizards', obj.captureList.lizardsList.map(item => item.pestsCnt), 2),
            generateDataset('Other Insects', obj.captureList.otherInsectsList.map(item => item.pestsCnt), 3),
            generateDataset('Rodents', obj.captureList.rodentsList.map(item => item.pestsCnt), 4),
          ],
        };

        return {...obj, config};
      }).filter(Boolean);

      setCustomer(customerList);
    }
  };

  // 3. 필터 변경 핸들러
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setParam(prev => {
      let nextDate = prev.startDate;

      // 타입을 바꿀 때 날짜 형식을 input type에 맞게 변환 (YYYY-MM-DD <-> YYYY-MM)
      if (name === 'type') {
        if (value === 'Weekly') {
          // 월 선택에서 일 선택으로 바뀔 때 오늘 날짜로 기본값 설정 (혹은 이전 값 유지 로직 가능)
          nextDate = new Date().toISOString().split('T')[0];
        } else {
          // 일 선택에서 월 선택으로 바뀔 때 YYYY-MM 포맷으로 자름
          nextDate = prev.startDate.slice(0, 7);
        }
      } else {
        nextDate = value;
      }

      return {
        ...prev,
        [name]: value,
        startDate: nextDate
      };
    });
  };

  return (
    <TrendsWrap>
      <CustomerHead title="Pest Trends" desc="Historical pest analysis" />
      <main className="content">
        <div className="trend-util">
          {/* 타입 선택 (Weekly/Monthly) */}
          <select name="type" value={param.type} onChange={handleFilterChange}>
            <option value="Weekly">Weekly</option>
            <option value="Monthly">Monthly</option>
          </select>

          {/* 동적 날짜 선택 (타입에 따라 달력 모양 변경) */}
          <input
            type={param.type === 'Weekly' ? 'date' : 'month'}
            id="trends-date"
            name="date"
            value={param.startDate}
            onChange={handleFilterChange}
          />
        </div>

        <ul className='user-list'>
          {customer?.map(item => (
            <li className='user-item' key={item.custMstSeq}>
              <div className='client-head'>
                <strong>{item.clientName}</strong>
              </div>
              <div className='client-con'>
                <ul className='client-info'>
                  <li>
                    <span className='tit'>Contract Start Date</span>
                    <p className='value'>{item?.contractStartDt}</p>
                  </li>
                  <li>
                    <span className='tit'>Contract End Date</span>
                    <p className='value'>{item?.contractEndDt}</p>
                  </li>
                  <li>
                    <span className='tit'>Number of Worker</span>
                    <p className='value'>{item?.workCnt}</p>
                  </li>
                </ul>
                <div className='client-chart'>
                  {/* 4. 선택된 타입에 따라 제목 동적 표시 */}
                  <strong>{param.type === 'Weekly' ? 'Weekly Trend' : 'Monthly Trend'}</strong>
                  <div style={{ height: '250px', marginTop: '15px' }}>
                    {item.config && <Line data={item.config} options={options} />}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </main>
    </TrendsWrap>
  )
}

const TrendsWrap = styled.div`
  position: relative;
  background: #F9FAFB;

  .content {
    padding: 20px;
    overflow-y:auto;

    .trend-util {
      position:relative;
      display:flex;
      align-items: center;
      gap:0 10px;
      
      select {
        width: 200px;
        height: 40px;
        line-height: 40px;
        padding: 0 16px;
        font-weight: 400;
        font-size: 16px;
        color: #777777;
        background: #fff url('https://ust-india.s3.ap-northeast-2.amazonaws.com/erp-web/sel_arrow.svg') top 50% right 10px no-repeat;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 5px;
        outline: none;
      }
      
      input {
        width: 160px;
        height: 40px;
        line-height: 40px;
        padding: 0 16px;
        font-weight: 400;
        font-size: 16px;
        color: #777777;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 5px;
        outline: none;
      }
      
      button {
        height: 40px;
        line-height: 40px;
        padding: 0 15px;
        display: flex;
        align-items: center;
        gap: 0 5px;
        background: #0072B9;
        border-radius: 5px;
        font-weight: 500;
        font-size: 14px;
        color: #ffffff;
      }
    }
    
    .user-list {
      margin-top: 10px;
      display: flex;
      flex-direction: column;
      gap: 20px;
      padding: 0;
      list-style: none;

      .user-item {
        background: #fff;
        border-radius: 12px;
        border: 1px solid #e5e7eb;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }
    }

    .client-head {
      padding: 15px 20px;
      border-bottom: 1px solid #f3f4f6;
      background: #f9fafb;

      strong {
        font-weight: 700;
        font-size: 16px;
        color: #111827;
      }
    }

    .client-con {
      padding: 20px;

      .client-info {
        padding: 0;
        list-style: none;
        li {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;

          .tit {
            font-size: 14px;
            color: #6b7280;
          }

          .value {
            font-weight: 600;
            font-size: 14px;
            color: #111827;
          }
        }
      }

      .client-chart {
        margin-top: 25px;

        strong {
          font-weight: 600;
          font-size: 15px;
          color: #374151;
        }
      }
    }
  }
`;
